import { useEffect, useMemo, useState } from "react"

import * as turf from "@turf/turf"
import { useParams } from "react-router-dom"

import { useGeoServerFeaturesList } from "@l2r-front/l2r-geodata"
import {
    ReactMapGl,
    Map as L2rMap,
    useMapDispatchContext,
    REFERENTIAL_MAP_ID,
    PADDING_BOUNDING_BOX,
    CANVAS_MIN_BBOX_WIDTH,
} from "@l2r-front/l2r-map"
import { PropTypes } from "@l2r-front/l2r-proptypes"

import { useNetworksStateContext } from "../../contexts/NetworksContext"

export function ReferentialMap(props) {

    const {
        children,
        initialBoundingBox,
        ...mapProps
    } = props

    const { selectedNetwork } = useNetworksStateContext()
    const { road } = useParams()
    const { getMapRef } = useMapDispatchContext()
    const mapRef = getMapRef(REFERENTIAL_MAP_ID)
    const [boundingBoxToFit, setBoundingBoxToFit] = useState()

    const { data: roadSegments, isLoading } = useGeoServerFeaturesList(
        ["networks", "list", { road: road }],
        {
            layer: selectedNetwork?.referential?.layerSection,
            linear_location_road: road,
        },
        {
            enabled: !!road && !!selectedNetwork?.referential,
        })

    const baseBoundingBox = useMemo(() => {
        if (initialBoundingBox) {
            return initialBoundingBox
        }
        if (isLoading) {
            return null
        }
        if (!roadSegments || !roadSegments.length) {
            const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
            return {
                minLng,
                minLat,
                maxLng,
                maxLat,
            }
        }

        const roadFeature = turf.featureCollection(roadSegments)
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(roadFeature)
        const lngOffset = Math.max((CANVAS_MIN_BBOX_WIDTH - (maxLng - minLng)), 0) / 2
        return ({
            minLng: minLng - lngOffset,
            minLat,
            maxLng: maxLng + lngOffset,
            maxLat,
        })
    }, [roadSegments, selectedNetwork, initialBoundingBox, isLoading])

    useEffect(() => {
        if (baseBoundingBox) {
            setBoundingBoxToFit(baseBoundingBox)
        }
    }, [baseBoundingBox, setBoundingBoxToFit])

    useEffect(() => {
        const map = mapRef?.getMap()
        if (boundingBoxToFit && map) {
            const frame = requestAnimationFrame(() => {
                map.resize()

                const horizontalPadding = Math.min(PADDING_BOUNDING_BOX, map.getCanvas().width / 8)
                const verticalPadding = Math.min(PADDING_BOUNDING_BOX, map.getCanvas().height / 8)

                map.fitBounds(
                    [
                        [boundingBoxToFit.minLng, boundingBoxToFit.minLat],
                        [boundingBoxToFit.maxLng, boundingBoxToFit.maxLat],
                    ],
                    {
                        duration: 0,
                        padding: {
                            top: verticalPadding,
                            bottom: verticalPadding,
                            left: horizontalPadding,
                            right: horizontalPadding,
                        },
                    },
                )

                setBoundingBoxToFit(null)
            })
            return () => cancelAnimationFrame(frame)
        }
    }, [setBoundingBoxToFit, boundingBoxToFit, mapRef])

    const mapInitialViewState = useMemo(() => {
        const [minLng, minLat, maxLng, maxLat] = turf.bbox(selectedNetwork.boundingBox)
        return {
            longitude: (minLng + maxLng) / 2,
            latitude: (minLat + maxLat) / 2,
            zoom: 8,
        }
    }, [selectedNetwork])

    if (isLoading) {
        return null
    }

    return (
        <L2rMap
            borderRounded={true}
            displayZoomButtons={false}
            displayRecenterButton={false}
            displayMapLayerSelector
            dragRotate={false}
            id={REFERENTIAL_MAP_ID}
            initialViewState={mapInitialViewState}
            isBaseMap={false}
            isLoading={false}
            logoPosition="top-right"
            {...mapProps}
        >
            {children}
        </L2rMap>
    )
}

ReferentialMap.propTypes = {
    ...ReactMapGl.propTypes,
    className: PropTypes.string,
    initialBoundingBox: PropTypes.object,
}